/**
 * F1.4 — capture UTM from the landing URL into sessionStorage for funnel events + checkout.
 * Spec: wiki 07-analityka/instrukcja-gtm.md (F1.4).
 */

export const UTM_STORAGE_KEY = 'mm_utm_params';

export const UTM_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export type UtmParamKey = (typeof UTM_PARAM_KEYS)[number];

export type UtmParams = Partial<Record<UtmParamKey, string>>;

function readSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Parse utm_* from a query string (with or without leading `?`). */
export function parseUtmFromSearch(search: string): UtmParams {
  const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
  const result: UtmParams = {};
  UTM_PARAM_KEYS.forEach(key => {
    const value = params.get(key)?.trim();
    if (value) result[key] = value;
  });
  return result;
}

export function hasAnyUtm(params: UtmParams | null | undefined): boolean {
  if (!params) return false;
  return UTM_PARAM_KEYS.some(key => Boolean(params[key]));
}

/** Persist UTM snapshot (overwrites previous). No-op when empty. */
export function writeStoredUtm(params: UtmParams): void {
  const storage = readSessionStorage();
  if (!storage || !hasAnyUtm(params)) return;
  storage.setItem(UTM_STORAGE_KEY, JSON.stringify(params));
}

export function readStoredUtm(): UtmParams {
  const storage = readSessionStorage();
  if (!storage) return {};
  try {
    const raw = storage.getItem(UTM_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as UtmParams;
    if (!parsed || typeof parsed !== 'object') return {};
    const result: UtmParams = {};
    UTM_PARAM_KEYS.forEach(key => {
      const value = parsed[key];
      if (typeof value === 'string' && value.trim()) result[key] = value.trim();
    });
    return result;
  } catch {
    return {};
  }
}

/**
 * On app entry / route change: if the URL has utm_*, store them.
 * Does not clear storage when the URL has no UTM (keep first-touch for the session).
 */
export function captureUtmFromLocation(search = typeof window !== 'undefined' ? window.location.search : ''): UtmParams {
  const fromUrl = parseUtmFromSearch(search);
  if (hasAnyUtm(fromUrl)) {
    writeStoredUtm(fromUrl);
    return fromUrl;
  }
  return readStoredUtm();
}

/** Flat utm_* object for dataLayer / checkout body (omit empty). */
export function getUtmPayload(): UtmParams {
  return readStoredUtm();
}

/** Preserve UTM across `sessionStorage.clear()` (e.g. logout). */
export function preserveUtmAcrossClear(clearFn: () => void): void {
  const utm = readStoredUtm();
  clearFn();
  if (hasAnyUtm(utm)) writeStoredUtm(utm);
}
